import type { INewsItem } from '../../../interfaces/News.interface';
import type { INewsCardData } from './types';
import type { ICompany } from './CompanyTag';


/**
 * Maps BE JSON structure (INewsCardData) to INewsItem (for backward compatibility)
 * This is used both for the Detail API and for the Public/Filter API responses,
 * so that the rest of the app can rely on a unified INewsItem shape.
 */
export const mapCardDataToNewsItem = (cardData: INewsCardData): INewsItem => {
  const firstCompany = cardData.companies?.[0];
  const changeValue = firstCompany?.percentage ?? 0;
  const changeString = `${changeValue >= 0 ? '+' : ''}${changeValue.toFixed(2)}%`;

  return {
    id: cardData.id,
    title: cardData.title,
    image: cardData.banner_link ?? undefined,
    body: cardData.content,
    stockName: firstCompany?.symbol,
    change: changeString,
    changeType: changeValue >= 0 ? 'positive' : 'negative',
    timestamp: cardData.created_at?.toString(),
    time: cardData.created_at?.toString(),
    // Preserve full companies metadata for trading flows
    companies: cardData.companies,
    // Surface tags and URL for consumers that need them
    tags: cardData.tags,
    url: cardData.url,
    source: cardData.more_info
      ? {
          name: cardData.more_info.source_name ?? '',
          avatar: cardData.more_info.source_logo,
        }
      : undefined,
  };
};

/**
 * Maps INewsItem to INewsCardData format for NewsCard component
 */
export const mapNewsItemToCardData = (item: INewsItem): INewsCardData => {
  // Parse percentage from change string (e.g., "+2.5%" or "-1.2%")
  const parsePercentage = (change?: string): number => {
    if (!change) return 0;
    const cleaned = change.replace(/[+%]/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const companies: ICompany[] | undefined = item.stockName
    ? [
        {
          symbol: item.stockName,
          percentage: parsePercentage(item.change),
        },
      ]
    : undefined;

  return {
    id: item.id,
    title: item.title,
    content: item.body,
    banner_link: item.image ?? null,
    created_at: item.timestamp ?? item.time,
    more_info: item.source
      ? {
          source_name: item.source.name,
          source_logo: item.source.avatar ?? item.source.icon,
        }
      : undefined,
    companies,
  };
};

